import { AnnotationLocation, DateInfo, TextLabel } from '@/api';
import { RefObject, useCallback, useEffect, useState } from 'react';

interface AnnotationRectangleCoordinates {
  left: number;
  top: number;
  width: number;
  height: number;
}

const calculateRectangleDimensions = (location: AnnotationLocation): AnnotationRectangleCoordinates | undefined => {
  if (!location || !location.topLeft || !location.topRight || !location.bottomLeft || !location.bottomRight) {
    return;
  }

  const { topLeft, topRight, bottomLeft, bottomRight } = location;

  const left = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x) * 100;
  const top = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y) * 100;
  const right = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x) * 100;
  const bottom = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y) * 100;

  const width = right - left;
  const height = bottom - top;

  return { left, top, width, height };
};

type ColorName = 'yellow' | 'lightgreen' | 'lightblue' | 'orange' | 'pink';

interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

const colors: Record<ColorName, RgbaColor> = {
  yellow: { r: 255, g: 255, b: 0, a: 1 },
  lightgreen: { r: 144, g: 238, b: 144, a: 1 },
  lightblue: { r: 173, g: 216, b: 230, a: 1 },
  orange: { r: 255, g: 165, b: 0, a: 1 },
  pink: { r: 255, g: 192, b: 203, a: 1 }
};

function getColorWithAlpha(colorName: ColorName, alpha: number): string {
  const color = colors[colorName];

  if (!color) {
    throw new Error(`Invalid color name: ${colorName}`);
  }

  const alphaValue = Math.min(1, Math.max(0, alpha));

  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alphaValue})`;
}

const FontWeight = '600';

interface UsePdfPageAnnotationProps {
  pageId: string;
  datesLabels?: DateInfo[];
  textLabels?: TextLabel[];
  height: number;
  width: number;
  showFilteredLabels: boolean;
  filterAnnotationLabels: FilterAnnotationLabels;
  pageInputRef: RefObject<HTMLDivElement | null>;
}

export const usePdfPageAnnotation = ({
  pageId,
  datesLabels,
  textLabels,
  height,
  width,
  showFilteredLabels,
  filterAnnotationLabels,
  pageInputRef
}: UsePdfPageAnnotationProps) => {
  const [rerenderTrigger, setRerenderTrigger] = useState<number>(Date.now());
  const [isAnnotationsLayerLoaded, setIsAnnotationsLayerLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (isAnnotationsLayerLoaded && pageInputRef.current) {
      const annotationLayer = pageInputRef.current.querySelector('.react-pdf__Page__annotations.annotationLayer');

      if (annotationLayer) {
        annotationLayer.innerHTML = '';

        if (showFilteredLabels) {
          textLabels?.forEach((textLabel) => {
            const rect = calculateRectangleDimensions(textLabel.location);
            const label = textLabel.labelValue;

            if (rect && filterAnnotationLabels[pageId]?.[label]) {
              const annotationElement = document.createElement('div');
              annotationElement.style.position = 'absolute';
              annotationElement.style.backgroundColor = getColorWithAlpha('pink', 0.5);
              annotationElement.style.left = `${rect.left}%`;
              annotationElement.style.top = `${rect.top}%`;
              annotationElement.style.width = `${rect.width}%`;
              annotationElement.style.height = `${rect.height}%`;
              annotationElement.style.userSelect = 'none';
              annotationElement.setAttribute('data-testid', textLabel.labelId);
              annotationElement.setAttribute('data-info', `Rectangle: ${textLabel.labelValue}`);

              const labelElement = document.createElement('div');
              labelElement.textContent = textLabel.labelValue;
              labelElement.style.position = 'absolute';
              labelElement.style.left = `${rect.left}%`;
              labelElement.style.top = `calc(${rect.top}% - 10px)`;
              labelElement.style.fontSize = '10px';
              labelElement.style.lineHeight = '10px';
              labelElement.style.backgroundColor = getColorWithAlpha('pink', 1);
              labelElement.style.fontWeight = FontWeight;
              labelElement.style.color = '#000';
              labelElement.style.userSelect = 'none';
              labelElement.setAttribute('data-testid', textLabel.labelId);

              annotationLayer.appendChild(annotationElement);
              annotationLayer.appendChild(labelElement);
            }
          });

          datesLabels?.forEach((dateItem) => {
            const rect = calculateRectangleDimensions(dateItem.location);
            const label = `${dateItem.date} / ${dateItem.dateCategory}`;

            if (rect && filterAnnotationLabels[pageId]?.[label]) {
              const annotationElement = document.createElement('div');
              annotationElement.style.position = 'absolute';
              annotationElement.style.backgroundColor = getColorWithAlpha('lightblue', 0.5);
              annotationElement.style.left = `${rect.left}%`;
              annotationElement.style.top = `${rect.top}%`;
              annotationElement.style.width = `${rect.width}%`;
              annotationElement.style.height = `${rect.height}%`;
              annotationElement.style.userSelect = 'none';
              annotationElement.setAttribute('data-testid', dateItem.dateId);
              annotationElement.setAttribute('data-info', `Rectangle: ${dateItem.date} / ${dateItem.dateCategory}`);

              const labelElement = document.createElement('div');
              labelElement.textContent = `${dateItem.date} / ${dateItem.dateCategory}`;
              labelElement.style.position = 'absolute';
              labelElement.style.left = `${rect.left}%`;
              labelElement.style.top = `calc(${rect.top}% - 10px)`;
              labelElement.style.fontSize = '10px';
              labelElement.style.lineHeight = '10px';
              labelElement.style.backgroundColor = getColorWithAlpha('lightblue', 1);
              labelElement.style.fontWeight = FontWeight;
              labelElement.style.color = '#000';
              labelElement.style.userSelect = 'none';
              labelElement.setAttribute('data-testid', dateItem.dateId);

              annotationLayer.appendChild(annotationElement);
              annotationLayer.appendChild(labelElement);
            }
          });
        }
      }
    }
  }, [
    rerenderTrigger,
    isAnnotationsLayerLoaded,
    datesLabels,
    textLabels,
    showFilteredLabels,
    filterAnnotationLabels,
    pageId,
    pageInputRef
  ]);

  useEffect(() => {
    setIsAnnotationsLayerLoaded(false);
    setRerenderTrigger(Date.now());
  }, [height, width]);

  const onGetAnnotationsSuccess = useCallback(() => {
    setIsAnnotationsLayerLoaded(true);
    setRerenderTrigger(Date.now());
  }, []);

  return { onGetAnnotationsSuccess };
};
